import { useEffect, useState } from "react";
import { TasksHeader } from "./TasksHeader";
import OffCanvas from "../../components/offCanvas";
import AddUpdateTask from "./AddUpdateTask";
import Toaster, { ToastConfig } from "../../components/Toaster";
import CustomButtonRenderer from "../../renderers/CustomButtonRenderer";
import { ErrorTypes } from "../../interfaces/errors";
import { formatDate, formatDateTask } from "../../helper/dateFormatter";
import { createTaskApi } from "../../api-helpers/TaskApiHelper";
import {
  CommentsBase,
  FilterCriteria,
  GridDataRequest,
  GridDataResponse,
  TaskDetailDto,
} from "tasks-experience-client";
import { CellClickedEvent, ColDef, ValueGetterParams } from "ag-grid-community";
import { GenericGrid } from "../../grid/GenericGrid";

import Lookups from "../../constants/Lookups";
import { PatientPersonalInfo } from "patients-experience-client";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import { LookupCellValueGetter } from "../../grid/renderers/LookupCellValueGetter";
import { WorkflowStateCellValueGetter } from "../../grid/renderers/WorkflowStateCellValueGetter";
import { UserContextHolder } from "../../auth/UserContextHolder";
import { AxiosResponse } from "axios";
import { GenericCommentsModal } from "../GenericCommentsModal";
import { useLocation } from "react-router-dom";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { WorkflowState } from "common-util-experience-client";

export interface Props {
  patientId?: string;
  breadcrumbs?: boolean | undefined;
}

export interface TaskList {
  sno: number;
  task: string;
  patient: string;
  taskType: string;
  source: string;
  dueDate: string;
  status: string;
  assignedTo: string;
  assignedOn: string;
  priority: string;
  id: string;
}

export const TasksGrid = ({ patientId, breadcrumbs }: Props) => {
  const patientApi = createPatientApi();
  const taskApi = createTaskApi();
  const gridApi = createGridApi();

  const [currentUserId, setCurrentUserId] = useState<string>();

  const [filters, setFilters] = useState<FilterCriteria[]>([]);

  const [initialFiltersReady, setInitialFiltersReady] = useState(false);
  const [gridReloadKey, setGridReloadKey] = useState(0);

  const [offCanvasComp, setOffCanvasComp] = useState("");
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [offCanvasTitle, setOffCanvasTitle] = useState("");
  const [offCanvasSubTitle, setOffCanvasSubTitle] = useState("");
  const [selectedTask, setSelectedTask] = useState<TaskDetailDto>(
    {} as TaskDetailDto,
  );
  const [selectedTaskId, setSelectedTaskId] = useState<string>();
  const [patientProfile, setPatientProfile] = useState<PatientPersonalInfo>();
  const [tasksWithComments, setTasksWithComments] = useState<any[]>([]);
  const [commentsToShow, setCommentsToShow] = useState<any>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [statuses, setStatuses] = useState<WorkflowState[] | null>();
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedId = queryParams.get("selectedId");

  useEffect(() => {
    if (selectedId) {
      handleUpdateTask(selectedId);
    }
  }, [selectedId]);

  useEffect(() => {
    UserContextHolder.addListener(() => {
      setCurrentUserId(UserContextHolder.getUser()?.id);
    });
  }, []);

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  useEffect(() => {
    const getPatientProfile = async () => {
      try {
        const response =
          await patientApi.patientsControllerGetPatientPersonalInfo(patientId!);
        setPatientProfile(response.data);
      } catch (error) {
        console.error("Error fetching Patients:", error);
      }
    };
    if (patientId) {
      getPatientProfile();
    }
  }, []);

  useEffect(() => {
    if (initialFiltersReady) {
      return;
    }

    if (!currentUserId) {
      return;
    }

    if (!(statuses?.length! > 0)) return;

    setInitialFiltersReady(true);
  }, [filters]);

  const loadTaskDetails = async (id: string) => {
    try {
      const response = await taskApi.taskControllerFindOne(id);
      await setSelectedTask(response.data);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const handleUpdateTask = async (taskId: string) => {
    setSelectedTaskId(taskId);
    await loadTaskDetails(taskId);
    handleOffCanvasShow("updateTask");
  };

  const handleOffCanvasShow = (comp: string) => {
    if (comp === "addTasks") {
      setOffCanvasTitle("Add Task/Orders");
      setOffCanvasSubTitle("Add task based on interaction");
    }
    if (comp === "updateTask") {
      setOffCanvasTitle("Update Task/Orders");
      setOffCanvasSubTitle("Edit task based on interaction");
    }
    setOffCanvasComp(comp);
    setOffCanvasShow(!offCanvasShow);
  };

  const openInNewTab = (url: string) => {
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (newWindow) newWindow.opener = null;
  };

  const colDefs: ColDef[] = [
    {
      field: "taskSubtype",
      headerName: "Task",
      width: 300,
      minWidth: 250,
      sortable: true,
      pinned: "left",
    },

    {
      field: "taskId",
      headerName: "",
      width: 80,
      minWidth: 80,
      pinned: "left",
      cellRenderer: (params: ValueGetterParams) => {
        const taskId = params.data.taskId;
        const selectedTask = tasksWithComments.filter(
          (task) => task.taskId === taskId,
        );
        return (
          <span
            className={
              selectedTask?.[0]?.comments?.commentsCount
                ? "cursor-pointer"
                : "invisible"
            }
            onClick={() => {
              setCommentsToShow(selectedTask[0]);
              setShowModal(true);
            }}
          >
            <span className="material-symbols-outlined fs-20 me-1 align-text-top">
              comment
            </span>
            {selectedTask?.[0]?.comments?.commentsCount ?? ""}
          </span>
        );
      },
    },
    {
      field: "patientName",
      headerName: "Patient",
      maxWidth: 230,
      pinned: "left",
      sortable: true,
      hide: patientId != undefined,
      cellRenderer: (params: ValueGetterParams) => {
        return (
          <a
            href="#"
            className="a-link a-underline text-secondary"
            onClick={() => {
              openInNewTab(`/patients/${params.data["patientId"]}/summary`);
            }}
          >
            {params.data[params.colDef.field!]}
          </a>
        );
      },
    },

    {
      field: "taskType",
      headerName: "Task Type",
      maxWidth: 220,
      sortable: true,
    },

    {
      field: "dueAt",
      headerName: "Due Date",
      maxWidth: 120,
      sortable: true,
      initialSort: "asc",
      valueFormatter: function (params) {
        return formatDateTask(params.data.dueAt); // Formats the date only
      },
    },
    {
      field: "wfStateName",
      headerName: "Status",
      maxWidth: 170,
      sortable: true,
      // valueGetter: (params: ValueGetterParams) => WorkflowStateCellValueGetter(params.data.wfDesignerId, params),
    },
    {
      field: "assignedUserName",
      headerName: "Assignee",
      minWidth: 110,
      sortable: true,
    },
    {
      field: "updatedAt",
      headerName: "Assigned On",
      maxWidth: 150,
      sortable: true,
      valueFormatter: function (params) {
        return formatDate(params.data.updatedAt);
      },
    },
    {
      field: "priorityName",
      headerName: "Priority",
      maxWidth: 120,
      sortable: true,
      cellRenderer: (params: ValueGetterParams) => {
        const priority = params.data.priorityName;

        if (priority === "Low") {
          return <span className="badge background-planned">{priority}</span>;
        }
        if (priority === "High") {
          return <span className="badge background-cancelled">{priority}</span>;
        }
        if (priority === "Medium") {
          return <span className="badge background-noshow">{priority}</span>;
        }
      },
    },
    {
      cellRenderer: CustomButtonRenderer,
      onCellClicked: (event: CellClickedEvent) => {
        handleUpdateTask(event.data.taskId);
      },
      maxWidth: 50,
      pinned: "right",
      sortable: false,
    },
  ];

  const handlerAddUpdateTaskForm = async (toasterConfig: ToastConfig) => {
    setOffCanvasShow(!offCanvasShow);
    setToastConfig(toasterConfig);
    setShowToast(true);
  };

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    const response = await gridApi.gridControllerGetTaskGridData(request);
    const tasksList = response.data.data;

    tasksList.forEach(async (task) => {
      const commentsData = await loadComments(task.taskId);

      setTasksWithComments((prevTasksWithComments) => {
        const existingTasks = prevTasksWithComments || [];

        const updatedTasks = existingTasks.map((t) =>
          t.taskId === task.taskId ? { ...t, comments: commentsData } : t,
        );

        const isTaskNew = !existingTasks.some((t) => t.taskId === task.taskId);
        if (isTaskNew) {
          updatedTasks.push({ ...task, comments: commentsData });
        }

        return updatedTasks;
      });
    });

    return response.data;
  };

  const loadComments = async (taskId: string) => {
    try {
      const response: AxiosResponse<CommentsBase[]> =
        await taskApi.taskControllerGetCommentsByTaskId(taskId);
      const obj = {
        commentsCount: response.data.length,
        comments: response.data,
      };
      return obj;
    } catch (ex: any) {
      console.error("Failed to load comments", ex);
      return { commentsCount: 0, comments: [] };
    }
  };

  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
          addClass={"mt-0 py-1 min-w-22-5"}
        />
      </div>
      {currentUserId && (
        <main className="main">
          <TasksHeader
            breadcrumbs={breadcrumbs}
            patientId={patientId}
            currentUserId={currentUserId}
            offCanvasShowCallback={handleOffCanvasShow}
            onUpdateFilters={setFilters}
            statuses={statuses}
            setStatuses={setStatuses}
          />
          <div className="d-flex px-3 px-md-4 px-lg-5 d-flex align-items-stretch">
            <div className="flex-fill">
              <div className="py-4">
                {initialFiltersReady && (
                  <GenericGrid
                    gridId="tasks-grid"
                    colDefs={colDefs}
                    dataProvider={loadData}
                    filters={filters}
                    key={gridReloadKey}
                  ></GenericGrid>
                )}
              </div>
            </div>
          </div>
          <OffCanvas
            title={offCanvasTitle}
            subTitle={offCanvasSubTitle}
            offCanvasShow={offCanvasShow}
            setOffCanvasShow={setOffCanvasShow}
            offCanvasComp={offCanvasComp}
          >
            {offCanvasComp === "addTasks" && (
              <AddUpdateTask
                patient={patientProfile}
                onSubmitTask={handlerAddUpdateTaskForm}
                setOffCanvasShow={setOffCanvasShow}
                isUpdate={false}
                reloadGrid={reloadGrid}
              />
            )}
            {offCanvasComp === "updateTask" && (
              <AddUpdateTask
                patient={patientProfile}
                setOffCanvasShow={setOffCanvasShow}
                onSubmitTask={handlerAddUpdateTaskForm}
                selectedTask={selectedTask}
                isUpdate={true}
                taskId={selectedTaskId}
                reloadGrid={reloadGrid}
                enableDueDate={true}
              />
            )}
          </OffCanvas>
        </main>
      )}

      <GenericCommentsModal
        addComments={false}
        subTitle={commentsToShow !== undefined && commentsToShow.taskSubtype}
        handleModal={() => setShowModal(!showModal)}
        show={showModal}
        title="Comments"
        data={commentsToShow}
      />
    </>
  );
};
